/**
 * Obras que não podem ser facturadas como deve ser.
 *
 * O `POR_APURAR` era, na prática, um estado permanente: as empresas ficavam
 * todas em «por apurar» e nada no sistema puxava por uma decisão. Uma obra
 * atravessava o percurso inteiro e era facturada sem ninguém ter apurado se o
 * cliente é substituto tributário — e facturar sem reter pode estar certo ou
 * ser dívida ao Estado, conforme o cliente.
 *
 * A lista é curta de propósito. Um cliente pessoa singular nunca aparece aqui:
 * a regra já o resolve sozinho, não é substituto tributário, não há nada a
 * decidir. Listar casos fechados é a maneira mais rápida de uma lista destas
 * deixar de ser lida.
 *
 * Ordena-se pelo que está em risco, e não por data: 416.000 Kz de retenção numa
 * obra de 6,4 milhões pesam mais do que um NIF mal escrito numa de 200.000.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, FileWarning, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const kz = (v) => `${Number(v || 0).toLocaleString('pt-PT')} Kz`;

const FiscalPorApurar = () => {
  const [lista, setLista] = useState(null);
  const navigate = useNavigate();

  const carregar = useCallback(() => {
    api.get('/projects/fiscal/por-apurar')
      .then((r) => setLista(r.data || []))
      .catch(() => setLista([]));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  if (!lista) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  const emRisco = lista.reduce((s, x) => s + Number(x.retencao_em_risco_kz || 0), 0);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <FileWarning className="w-6 h-6 text-amber-400" /> Perfis fiscais por apurar
        </h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          Obras a caminho da facturação cujo perfil fiscal ainda não permite
          emitir como deve ser. Enquanto estiverem aqui, a factura sai sem
          retenção na fonte — o que pode estar certo, ou ser dívida ao Estado.
        </p>
      </div>

      {lista.length === 0 ? (
        <div className="p-6 rounded-xl border border-slate-800 bg-slate-900 text-center text-slate-400 text-sm">
          Nada por apurar. Todas as obras em curso podem ser facturadas.
        </div>
      ) : (
        <>
          {emRisco > 0 && (
            <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-sm text-amber-200">
              <strong className="tabular-nums">{kz(emRisco)}</strong> de retenção na fonte
              dependem destas decisões.
            </div>
          )}

          <ul className="space-y-2">
            {lista.map((x) => (
              <li key={`${x.project_id}-${x.papel}`}
                className="p-4 rounded-xl border border-slate-800 bg-slate-900 space-y-2">
                <div className="flex gap-3 items-start flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-semibold text-slate-100">{x.titulo}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      {x.codigo} · {x.contrato} · {kz(x.valor_kz)}
                    </div>
                  </div>
                  {Number(x.retencao_em_risco_kz) > 0 && (
                    <div className="text-right">
                      <div className="text-amber-400 font-semibold tabular-nums">
                        {kz(x.retencao_em_risco_kz)}
                      </div>
                      <div className="text-[11px] text-slate-500">retenção em causa</div>
                    </div>
                  )}
                </div>

                <div className="text-sm text-slate-300">
                  <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-2">
                    {x.papel === 'CLIENTE' ? 'Cliente' : 'Executante'}
                  </span>
                  {x.nome}
                  {x.nif && <span className="text-slate-500 font-mono text-xs"> · {x.nif}</span>}
                </div>

                <ul className="text-xs text-amber-400/90 space-y-0.5">
                  {x.motivos.map((m) => <li key={m}>— {m}</li>)}
                </ul>

                <button
                  onClick={() => navigate(`/admin/projects?obra=${x.project_id}`)}
                  className="min-h-[40px] px-3 rounded-lg bg-slate-800 text-slate-200 text-xs
                             inline-flex items-center gap-1.5">
                  Abrir a obra <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default FiscalPorApurar;
